// src/pages/DataRoomPage.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFilePdf, FaFileWord, FaLock, FaDownload, FaChartLine, FaShieldAlt } from 'react-icons/fa';

const DataRoomPage = () => {
  // Toggle for "request access" simulation
  const [accessGranted, setAccessGranted] = useState(true); 

  const categories = [
    {
      id: 'strategy',
      title: 'Strategic Vision & National Impact',
      icon: <FaShieldAlt />,
      docs: [
        {
          title: 'Executive Project Overview',
          type: 'docx',
          desc: 'Comprehensive initiative description, key components (Railway, Sinter Plant), and project stages.',
          link: 'https://docs.google.com/document/d/10dDh-k1o_Ewu4HMRySi9jXUzqLB5fufX/edit?usp=drive_link&ouid=110812136038279325502&rtpof=true&sd=true'
        },
        {
          title: 'The "Northern Crucible" National Idea',
          type: 'docx',
          desc: 'Social-economic development plan focusing on innovation, Indigenous stewardship, and post-capitalist leadership.',
          link: 'https://docs.google.com/document/d/1msPx72vSju6imTvrZam0-Oc5PLHaxkj2/edit?usp=drive_link&ouid=110812136038279325502&rtpof=true&sd=true'
        },
        {
          title: 'Investor & Government Strategy',
          type: 'docx',
          desc: 'Draft correspondence and stakeholder engagement strategy for key government and industry partners.',
          link: 'https://docs.google.com/document/d/1qw0WQLudovg9g-umdSyOmVt6b6bY9-Du/edit?usp=drive_link&ouid=110812136038279325502&rtpof=true&sd=true'
        },
        {
          title: 'Elevator Pitches',
          type: 'docx',
          desc: 'Tailored narratives and pitches for ROI-focused investors, government officials, and partners.',
          link: 'https://docs.google.com/document/d/1Civ_VshiXGtwO6rOMQt19BpE43mzfZMW/edit?usp=sharing&ouid=110812136038279325502&rtpof=true&sd=true'
        }
      ]
    },
    {
      id: 'finance',
      title: 'Financials & Investment Scale',
      icon: <FaChartLine />,
      docs: [
        {
          title: 'Initial Articles of Expense (Angel Phase)',
          type: 'docx',
          desc: 'Breakdown of the seed funding required for foundational planning and stakeholder engagement.',
          link: 'https://docs.google.com/document/d/1jK7zIzccRbXUUGi8-w-iatczMBK0WaiI/edit?usp=drive_link&ouid=110812136038279325502&rtpof=true&sd=true'
        },
        {
          title: 'CapEx & Operational Cost Estimates',
          type: 'docx',
          desc: 'Order of magnitude costs for the 7000km railway ($35B+) and industrial clusters.',
          link: 'https://docs.google.com/document/d/18KTzcpwgUzTZznlTFn8Bs01P-5J7ygC5/edit?usp=drive_link&ouid=110812136038279325502&rtpof=true&sd=true'
        },
        {
          title: 'Borealis Industry Cluster Trajectory',
          type: 'pdf',
          desc: 'Economic ripple effects: From Sinter Plants to Data Centers and Space Infrastructure.',
          link: 'https://drive.google.com/file/d/1HlzuFstHP81DSl3ChGPFQ8DqPQMt0RsL/view?usp=drive_link'
        },
        {
          title: 'Potential Investors List',
          type: 'docx',
          desc: 'Targeted list of potential financial partners and strategic stakeholders.',
          link: 'https://docs.google.com/document/d/16Fq2NDmovcHpz1jrLUcHRzuZdcR9-G-I/edit?usp=sharing&ouid=110812136038279325502&rtpof=true&sd=true'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Feasibility & Engineering',
      icon: <FaFilePdf />,
      docs: [
        {
          title: 'Pre-Feasibility: Sintering Ore Plant',
          type: 'docx',
          desc: 'Technical assessment of establishing iron ore processing in Nunavut, including market analysis.',
          link: 'https://docs.google.com/document/d/14tIJo80zPMQvJlx3g0DfkXo4xzhlhWkU/edit?usp=drive_link&ouid=110812136038279325502&rtpof=true&sd=true'
        },
        {
          title: 'Polymetal Processing Analysis',
          type: 'docx',
          desc: 'Feasibility analysis of extending operations to copper and zinc processing alongside iron ore.',
          link: 'https://docs.google.com/document/d/1SbmPYY5Kzr0scnuqlhgRD7NalVi8Sj5v/edit?usp=drive_link&ouid=110812136038279325502&rtpof=true&sd=true'
        },
        {
          title: 'List of Qualified Bidders & Contractors',
          type: 'docx', // The link is a view link, but usually implies a doc/pdf
          desc: 'Database of potential partners for Geological Surveys, Railroad Design, and Modular Construction.',
          link: 'https://drive.google.com/file/d/1JSgXy14pioOvJcxUdbcJ4SwyuZaUGF3d/view?usp=drive_link'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0b1021] text-gray-200 font-sans">
      
      {/* HEADER */}
      <header className="border-b border-gray-800 bg-[#050a14] pt-24 pb-12 px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-400 text-xs font-bold uppercase tracking-widest mb-4">
          <FaLock size={10} /> Secure Repository
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
          Borealis <span className="text-[#00d2ff]">Data Room</span>
        </h1>
        <p className="max-w-2xl mx-auto text-gray-400 text-lg">
          Access detailed feasibility studies, financial projections, and strategic roadmaps for the Project Borealis initiative.
        </p>
      </header>

      {/* MAIN CONTENT */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        
        {/* ACCESS NOTICE */}
        {!accessGranted ? (
          <div className="text-center py-20 bg-[#111827] rounded-xl border border-gray-700">
            <FaLock className="mx-auto text-5xl text-gray-600 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Restricted Access</h2>
            <p className="text-gray-400 mb-6">You must be an accredited investor or partner to view these documents.</p>
            <button onClick={() => setAccessGranted(true)} className="bg-[#00d2ff] text-black font-bold py-3 px-8 rounded hover:bg-cyan-400 transition">
              Request Access
            </button>
          </div>
        ) : (
          <div className="grid gap-12">
            
            {/* DOCUMENT CATEGORIES */}
            {categories.map((cat) => (
              <div key={cat.id} className="animate-fade-in-up">
                <div className="flex items-center gap-3 mb-6 border-b border-gray-800 pb-2">
                  <span className="text-[#00d2ff] text-xl">{cat.icon}</span>
                  <h2 className="text-2xl font-bold text-white">{cat.title}</h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cat.docs.map((doc, idx) => (
                    <div key={idx} className="group relative bg-[#1f2937] p-6 rounded-lg border border-gray-700 hover:border-[#00d2ff] transition-all duration-300 hover:-translate-y-1">
                      <div className="absolute top-4 right-4 text-gray-600 group-hover:text-[#00d2ff] transition">
                        {doc.type === 'pdf' ? <FaFilePdf size={24} /> : <FaFileWord size={24} />}
                      </div>
                      
                      <h3 className="text-lg font-bold text-white pr-8 mb-2 group-hover:text-[#00d2ff] transition">
                        {doc.title}
                      </h3>
                      
                      <p className="text-sm text-gray-400 mb-6 leading-relaxed">
                        {doc.desc}
                      </p>

                      <a 
                        href={doc.link} 
                        target="_blank" 
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-bold text-gray-300 group-hover:text-white transition"
                      >
                        <FaDownload /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* CONTACT / NEXT STEPS */}
            <div className="mt-12 p-8 bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-2xl border border-blue-500/30 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Discuss Partnership?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                "We seek a collaborative partnership to realize this ambitious vision for the benefit of all Canadians."
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/contact" className="bg-[#00d2ff] text-black font-bold py-3 px-8 rounded hover:bg-cyan-400 transition">
                  Contact Steering Committee
                </Link>
                <a href="https://drive.google.com/drive/folders/1lZKxKrKR42itZ68sC1wT48kVP-orLuOC?usp=sharing" target="_blank" rel="noreferrer" className="border border-[#00d2ff] text-[#00d2ff] font-bold py-3 px-8 rounded hover:bg-[#00d2ff] hover:text-black transition">
                  Access Full Drive
                </a>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default DataRoomPage;